import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Wrench, 
  PaintBucket, 
  Zap, 
  Droplets, 
  Home, 
  Hammer,
  ArrowRight,
  Star
} from 'lucide-react';

export const ServicesSection = ({ onPostProjectClick }) => {
  const services = [
    {
      icon: Home,
      title: "Kitchen Remodeling",
      description: "Complete kitchen renovations, cabinet installation, countertop replacement, and appliance upgrades.",
      avgBids: "4-7 bids",
      avgSavings: "15-25%",
      popular: true,
      color: "from-orange-400 to-red-500"
    },
    {
      icon: Droplets,
      title: "Bathroom Repairs",
      description: "Plumbing fixes, tile work, fixture replacement, and complete bathroom renovations.",
      avgBids: "3-6 bids",
      avgSavings: "12-20%",
      popular: true,
      color: "from-blue-400 to-cyan-500"
    },
    {
      icon: Zap,
      title: "Electrical Work",
      description: "Wiring repairs, outlet installation, lighting upgrades, and electrical panel replacements.",
      avgBids: "2-5 bids",
      avgSavings: "10-18%",
      popular: false,
      color: "from-yellow-400 to-orange-500"
    },
    {
      icon: PaintBucket,
      title: "Painting & Decorating",
      description: "Interior and exterior painting, wallpaper installation, and decorative finishes.",
      avgBids: "5-8 bids",
      avgSavings: "20-30%",
      popular: false,
      color: "from-purple-400 to-pink-500"
    },
    {
      icon: Wrench,
      title: "General Repairs",
      description: "Handyman services, door repairs, window fixes, and general maintenance work.",
      avgBids: "3-6 bids",
      avgSavings: "15-22%",
      popular: true,
      color: "from-green-400 to-emerald-500"
    },
    {
      icon: Hammer,
      title: "Home Improvements",
      description: "Flooring installation, deck building, room additions, and structural modifications.",
      avgBids: "2-4 bids",
      avgSavings: "12-25%",
      popular: false,
      color: "from-indigo-400 to-purple-500"
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Star size={16} />
            <span>Popular Services</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Home Services That Get{' '}
            <span className="text-emerald-600">Multiple Bids</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From small repairs to major renovations, our platform covers all your home improvement needs. 
            See average savings and bid counts for each service category.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <Card key={index} className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 shadow-lg overflow-hidden">
              <CardContent className="p-0">
                {/* Header with gradient */}
                <div className={`bg-gradient-to-r ${service.color} p-6 relative`}>
                  {service.popular && (
                    <Badge className="absolute top-4 right-4 bg-white/20 text-white border-white/30 backdrop-blur-sm">
                      Popular
                    </Badge>
                  )}
                  <div className="flex items-center space-x-4">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                      <service.icon className="text-white" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-white">
                      {service.title}
                    </h3>
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-lg font-bold text-gray-900">
                        {service.avgBids}
                      </div>
                      <div className="text-xs text-gray-600">
                        Avg. Bids
                      </div>
                    </div>
                    <div className="text-center p-3 bg-emerald-50 rounded-lg">
                      <div className="text-lg font-bold text-emerald-600">
                        {service.avgSavings}
                      </div>
                      <div className="text-xs text-gray-600">
                        Avg. Savings
                      </div>
                    </div>
                  </div>
                  
                  {/* CTA */}
                  <button
                    onClick={() => {
                      if (onPostProjectClick) {
                        onPostProjectClick();
                      } else {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl font-medium transition-all duration-200 group-hover:bg-emerald-600 flex items-center justify-center space-x-2"
                  >
                    <span>Get Bids Now</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Don't See Your Service Listed?
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              We work with service providers across all home improvement categories. 
              Post your project and discover qualified professionals in your area.
            </p>
            <button
              onClick={() => {
                if (onPostProjectClick) {
                  onPostProjectClick();
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105"
            >
              Post Any Project
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};